import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import GuideLayout from "./GuideLayout";

const META_DESC =
  "Search GitHub repositories by keyword from this app: repository mode, repo cards, stars, forks, languages, sorting, and opening details.";

const GuideSearchRepositories = () => {
  useEffect(() => {
    const script = document.createElement("script");
    script.id = "gf-article-repo-search-jsonld";
    script.type = "application/ld+json";
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "TechArticle",
      headline: "How to search GitHub repositories with keywords and filters",
      description: META_DESC,
      author: { "@type": "Organization", name: "GitHub Finder" },
    });
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById("gf-article-repo-search-jsonld");
      if (el) el.remove();
    };
  }, []);

  return (
    <GuideLayout
      title="How to search GitHub repositories"
      description={META_DESC}
      eyebrow="Repository search"
      breadcrumb={[
        { to: "/", label: "Home" },
        { to: "/guide", label: "Guides" },
        { label: "Repositories" },
      ]}
    >
      <article className="gf-guide-prose" itemScope itemType="https://schema.org/TechArticle">
        <meta itemProp="headline" content="How to search GitHub repositories with keywords and filters" />
        <p className="gf-guide-prose__lead">
          Repository discovery is most useful when you combine a <strong>clear keyword</strong> (product area,
          framework, or problem domain) with <strong>sorting</strong> and a quick scan of quality signals: stars, forks,
          default branch activity, and primary language.
        </p>

        <h2>1. Switch to Repositories mode</h2>
        <p>
          On the home page, select <strong>Repositories</strong> so the main search targets public repositories rather
          than user profiles. Use the same search box for <strong>keywords, topics, and technologies</strong>—think how
          maintainers title and describe their projects on GitHub.
        </p>

        <h2>2. Understand each repo card</h2>
        <p>
          Each card summarises the owner avatar, name, description, <strong>stars</strong>, <strong>forks</strong>, and{" "}
          <strong>language</strong> when detected. The <strong>updated</strong> time helps you avoid abandoned forks
          when you need actively maintained code.
        </p>

        <h2>3. Sort and paginate</h2>
        <p>
          Use the <strong>sort</strong> control (for example most stars or recent updates) to match your goal: popularity
          for learning resources, recency for fast-moving ecosystems. Pagination keeps result browsing clean and
          predictable.
        </p>

        <h2>4. Open details</h2>
        <p>
          Use <strong>View details</strong> to open the in-app repository page for a structured summary, or jump to the
          canonical project on github.com when you need issues, discussions, or CI configuration.
        </p>

        <h2>5. Developer-first workflow</h2>
        <p>
          You can still start from <strong>Developers</strong> mode, open a user, and browse their repositories with
          stars and sort options—ideal when you already care about a specific maintainer or organisation.
        </p>

        <ul>
          <li>
            <Link to="/guide/find-github-developers">Find GitHub developers</Link> — filters and profile context.
          </li>
          <li>
            <Link to="/guide/faq">FAQ</Link>
          </li>
        </ul>

        <p className="gf-guide-prose__cta">
          <Link to="/" className="gf-btn gf-btn--primary gf-btn--sm">
            Search repositories
          </Link>
        </p>
      </article>
    </GuideLayout>
  );
};

export default GuideSearchRepositories;
